import { type Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';
import { AllocationSummary, SuggestNameButton } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import { computeAllocationSummary } from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface Props {
    locationCages: OverviewCage[];
    sources: WizardSource[];
    destinations: WizardDestination[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Merge mode — Step 2. There is always exactly one destination in this
 * mode: the cage that ends up holding the merged population. We auto-create
 * it on mount if it isn't there yet (e.g. fresh entry into the step) so the
 * form renders immediately instead of starting on an empty state.
 *
 * The cage dropdown is filtered to cages that are either empty or
 * currently occupied by one of the listed sources. Cages held by an
 * unlisted FG are intentionally excluded from the options: to merge that
 * FG too, the operator must go back to the sources step and add it
 * explicitly.
 */
export default function MergeDestinationStep(props: Props) {
    const { locationCages, sources, destinations, populationById, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    const allocationSummary = computeAllocationSummary({
        sources,
        destinations,
        populationById,
    });

    // Auto-create the single destination on first render of this step. The
    // reducer's `addDestination` in merge mode auto-fills the inflows from
    // every source — exactly what we want here.
    useEffect(() => {
        if (destinations.length === 0) {
            dispatch({ type: 'addDestination' });
        }
    }, [destinations.length, dispatch]);

    const destination = destinations[0];
    if (!destination) {
        // Effect will dispatch on next render; nothing to show this tick.
        return null;
    }

    const sourceIds = new Set(sources.map((s) => s.fishGroupId));
    const eligibleCages = locationCages.filter((cage) => {
        if (cage.activeFishGroup === null) return true;
        return sourceIds.has(cage.activeFishGroup.id);
    });

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>{t('actions.operate.merge.destination.heading')}</h4>
                <p className={styles.description}>
                    {t('actions.operate.merge.destination.description')}
                </p>
            </div>

            <AllocationSummary
                summary={allocationSummary}
                sources={sources}
                populationById={populationById}
                titleKey="actions.operate.allocation.combinedSourcesTitle"
            />

            <Card as="article" radius="sm" padding="xs" className={styles.destinationCard}>
                <div className={styles.fieldsGrid}>
                    <FormField
                        size="sm"
                        htmlFor={`${destination.tempId}-name`}
                        label={t('actions.operate.destinations.fields.name')}
                        labelAction={
                            <SuggestNameButton
                                cageName={
                                    locationCages.find((c) => c.cageId === destination.cageId)
                                        ?.cageName
                                }
                                operation="merge"
                                onApply={(value) =>
                                    dispatch({
                                        type: 'setDestinationName',
                                        tempId: destination.tempId,
                                        value,
                                    })
                                }
                            />
                        }>
                        <TextField
                            id={`${destination.tempId}-name`}
                            type="text"
                            placeholder={t('actions.operate.destinations.fields.namePlaceholder')}
                            value={destination.name}
                            onChange={(e) =>
                                dispatch({
                                    type: 'setDestinationName',
                                    tempId: destination.tempId,
                                    value: e.target.value,
                                })
                            }
                        />
                    </FormField>

                    <FormField
                        size="sm"
                        htmlFor={`${destination.tempId}-cage`}
                        label={t('actions.operate.destinations.fields.cage')}
                        hint={t('actions.operate.merge.destination.cageHint')}>
                        <Select
                            id={`${destination.tempId}-cage`}
                            value={destination.cageId == null ? '' : String(destination.cageId)}
                            onChange={(e) =>
                                dispatch({
                                    type: 'setDestinationCage',
                                    tempId: destination.tempId,
                                    value: e.target.value === '' ? null : Number(e.target.value),
                                })
                            }>
                            <option value="">
                                {t('actions.operate.destinations.fields.cagePlaceholder')}
                            </option>
                            {eligibleCages.map((cage) => (
                                <option key={cage.cageId} value={cage.cageId}>
                                    {formatMergeCageOption(cage, sourceIds, t)}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField
                        size="sm"
                        htmlFor={`${destination.tempId}-count`}
                        label={t('actions.operate.destinations.fields.estimatedCount')}
                        hint={t('actions.operate.destinations.fields.estimatedCountHint')}>
                        <TextField
                            id={`${destination.tempId}-count`}
                            type="number"
                            min={1}
                            value={destination.estimatedFishCount}
                            onChange={(e) =>
                                dispatch({
                                    type: 'setDestinationEstimatedCount',
                                    tempId: destination.tempId,
                                    value: e.target.value,
                                })
                            }
                        />
                    </FormField>

                    <FormField
                        size="sm"
                        htmlFor={`${destination.tempId}-notes`}
                        label={t('actions.operate.destinations.fields.notes')}
                        className={styles['field--fullWidth']}>
                        <TextArea
                            id={`${destination.tempId}-notes`}
                            rows={2}
                            value={destination.notes}
                            onChange={(e) =>
                                dispatch({
                                    type: 'setDestinationNotes',
                                    tempId: destination.tempId,
                                    value: e.target.value,
                                })
                            }
                        />
                    </FormField>
                </div>

                <div className={styles.inflowsBlock}>
                    <span className={styles.label}>
                        {t('actions.operate.merge.destination.absorbing')}
                    </span>
                    <ul className={styles.absorbingList}>
                        {sources.map((s) => (
                            <li key={s.fishGroupId}>{s.name}</li>
                        ))}
                    </ul>
                </div>
            </Card>
        </section>
    );
}

function formatMergeCageOption(
    cage: OverviewCage,
    sourceIds: Set<number>,
    t: (key: string, options?: Record<string, unknown>) => string
): string {
    if (cage.activeFishGroup === null) {
        return t('actions.operate.destinations.cageOption.empty', { cageName: cage.cageName });
    }
    if (sourceIds.has(cage.activeFishGroup.id)) {
        return t('actions.operate.destinations.cageOption.occupiedBySource', {
            cageName: cage.cageName,
            fishGroupName: cage.activeFishGroup.name,
        });
    }
    // Defensive — should be filtered out before this is called.
    return cage.cageName;
}
